import axios from "axios";
import Cookies from "js-cookie";

export const CALL_CENTER_SUCCESS_CODE = 600;

/**
 * @readonly
 * @enum {string}
 */
export const CallType = {
  MANUAL: "MANUAL",
  AI_AUTOMATED: "AI_AUTOMATED",
};

/**
 * @readonly
 * @enum {string}
 */
export const CallStatus = {
  INITIATED: "INITIATED",
  RINGING: "RINGING",
  ANSWERED: "ANSWERED",
  FAILED: "FAILED",
  COMPLETED: "COMPLETED",
};

/**
 * @typedef {Object} ManualCallStatusUpdateRequest
 * @property {'INITIATED'|'RINGING'|'ANSWERED'|'FAILED'|'COMPLETED'} status
 * @property {string} [eventTime] ISO-8601
 * @property {string} [recordingUrl]
 * @property {string} [summary]
 */

/**
 * @typedef {Object} ManualCallStatusByAccountRequest
 * @property {string} accountNo
 * @property {'INITIATED'|'RINGING'|'ANSWERED'|'FAILED'|'COMPLETED'} status
 * @property {string} [eventTime] ISO-8601
 * @property {string} [recordingUrl]
 * @property {string} [summary]
 */

/**
 * @readonly
 * @enum {string}
 */
export const TicketPriority = {
  LOW: "LOW",
  MEDIUM: "MEDIUM",
  HIGH: "HIGH",
  CRITICAL: "CRITICAL",
};

/**
 * @readonly
 * @enum {string}
 */
export const DefaultTicketStatusKey = {
  OPEN: "OPEN",
  FOLLOW_UP: "FOLLOW_UP",
  PAID: "PAID",
  FAILED: "FAILED",
  CALLBACK_REQUIRED: "CALLBACK_REQUIRED",
};

/**
 * @typedef {Object} ApiEnvelopeHeader
 * @property {number} code
 */

/**
 * @typedef {Object} ApiEnvelopeBody
 * @property {*} [value]
 * @property {string} [error]
 */

/**
 * @typedef {Object} ApiEnvelope
 * @property {ApiEnvelopeHeader} [header]
 * @property {ApiEnvelopeBody} [body]
 */

export const getCallCenterBaseUrl = () => {
  const base = process.env.REACT_APP_BASE_BACKEND_API_URL || "";
  return base.endsWith("/") ? base : `${base}/`;
};

/**
 * Same pattern as other service modules (e.g. Task.js, Configuration.js):
 * `Authorization: Bearer ${Cookies.get("accessToken")}`.
 * @param {Record<string, string>} [extraHeaders]
 * @returns {Record<string, string>}
 */
export const buildAuthHeaders = (extraHeaders = {}) => {
  const token = Cookies.get("accessToken");
  const headers = { ...extraHeaders };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

export const isSuperAdminUser = () => {
  try {
    return Cookies.get("userType") === "super_admin";
  } catch {
    return false;
  }
};

export const getCallCenterApiErrorMessage = (envelope, fallbackMessage = "Request failed") => {
  if (!envelope || typeof envelope !== "object") {
    return fallbackMessage;
  }
  const bodyError = envelope?.body?.error;
  const bodyValue = envelope?.body?.value;
  if (typeof bodyError === "string" && bodyError.trim()) return bodyError;
  if (typeof bodyValue === "string" && bodyValue.trim()) return bodyValue;
  return fallbackMessage;
};

/**
 * @template T
 * @param {ApiEnvelope} envelope
 * @param {string} [fallbackMessage]
 * @returns {T}
 */
export const unwrapEnvelopeValue = (envelope, fallbackMessage = "Request failed") => {
  const code = envelope?.header?.code;
  if (code === CALL_CENTER_SUCCESS_CODE) {
    return envelope?.body?.value;
  }

  const error = new Error(getCallCenterApiErrorMessage(envelope, fallbackMessage));
  error.responseCode = code;
  error.envelope = envelope;
  throw error;
};

const callCenterClient = axios.create({
  baseURL: `${getCallCenterBaseUrl()}api/`,
});

callCenterClient.interceptors.request.use((config) => {
  const token = Cookies.get("accessToken");
  if (!token) {
    return config;
  }
  const bearer = `Bearer ${token}`;
  if (config.headers && typeof config.headers.set === "function") {
    config.headers.set("Authorization", bearer);
  } else {
    const next = { ...(config.headers || {}) };
    next.Authorization = bearer;
    config.headers = next;
  }
  return config;
});

export const requestCallCenter = async (config) => {
  const response = await callCenterClient.request(config);
  return response.data;
};
